/**
 * Per-client aggregate wave dispatch + idle prefetch of other tabs and
 * last_purchase_detail. Keeps analyticsView thin.
 */
import {
  AGGREGATE_WAVES,
  getDashboardAggregate,
  getLastPurchaseDetail,
} from "../../../store/thunks/portalManagementThunk";
import {
  IDLE_PREFETCH_LANDING_HEADROOM_MS,
  IDLE_PREFETCH_STAGGER_MS,
  IDLE_PREFETCH_TABS,
} from "./analyticsTabMap";

type AppDispatch = (action: any) => any;
type DispatchTabWavesArgs = {
  dispatch: AppDispatch;
  customerId: string | number;
  tab: string;
};

type IdlePrefetchArgs = {
  dispatch: AppDispatch;
  customerId: string | number;
  /** Tab the user is already on — skipped in the idle queue. */
  activeTab: string;
};

type Session = {
  customerId: string | number | null;
  fetchedTabs: Record<string, boolean>;
  requestedKeys: Set<string>;
  saleLeadRequested: boolean;
  idleScheduled: boolean;
  generation: number;
  timers: ReturnType<typeof setTimeout>[];
};

const session: Session = {
  customerId: null,
  fetchedTabs: {},
  requestedKeys: new Set(),
  saleLeadRequested: false,
  idleScheduled: false,
  generation: 0,
  timers: [],
};

export const cancelIdlePrefetch = () => {
  session.timers.forEach(clearTimeout);
  session.timers = [];
  session.generation += 1;
  session.idleScheduled = false;
};

/** Drop the in-memory wave session so the next dispatchTabWaves refetches. */
export const resetAggregateSessionForClient = (customerId: string | number) => {
  cancelIdlePrefetch();
  session.customerId = customerId;
  session.fetchedTabs = {};
  session.requestedKeys = new Set();
  session.saleLeadRequested = false;
  session.idleScheduled = false;
};

const ensureClient = (customerId: string | number) => {
  if (session.customerId !== customerId) {
    resetAggregateSessionForClient(customerId);
  }
};

const filterNeededKeys = (keys: string[]): string[] =>
  keys.filter((key) => !session.requestedKeys.has(key));

/** Dispatch aggregate waves for a tab, skipping keys already requested this client. */
export const dispatchTabWaves = ({
  dispatch,
  customerId,
  tab,
}: DispatchTabWavesArgs): void => {
  ensureClient(customerId);
  if (session.fetchedTabs[tab]) return;
  session.fetchedTabs[tab] = true;

  const waves = AGGREGATE_WAVES[tab] ?? [];
  waves.forEach((wave) => {
    const keys = filterNeededKeys(wave);
    if (!keys.length) return;
    keys.forEach((k) => session.requestedKeys.add(k));
    dispatch(
      getDashboardAggregate({
        customer_id: customerId,
        keys,
      })
    );
  });
};

const warmSaleLeadDownload = (
  dispatch: AppDispatch,
  customerId: string | number
) => {
  if (session.saleLeadRequested) return;
  session.saleLeadRequested = true;
  dispatch(getLastPurchaseDetail({ customer_id: String(customerId) }));
};

const scheduleStep = (
  generation: number,
  delayMs: number,
  run: () => void
) => {
  const id = setTimeout(() => {
    if (generation !== session.generation) return;
    run();
  }, delayMs);
  session.timers.push(id);
};

/**
 * After landing waves are kicked off, stagger remaining tabs' deltas then
 * last_purchase_detail. Cancelled on client change / unmount.
 */
export const scheduleIdlePrefetch = ({
  dispatch,
  customerId,
  activeTab,
}: IdlePrefetchArgs): void => {
  ensureClient(customerId);
  if (session.idleScheduled) return;
  session.idleScheduled = true;
  const generation = session.generation;
  let step = 0;
  const headroom =
    activeTab === "landing" ? IDLE_PREFETCH_LANDING_HEADROOM_MS : 0;

  IDLE_PREFETCH_TABS.forEach((tab) => {
    if (tab === activeTab) return;
    const delay = headroom + IDLE_PREFETCH_STAGGER_MS * (step + 1);
    step += 1;
    scheduleStep(generation, delay, () => {
      // Allow re-dispatch if user already opened the tab (fetchedTabs true with
      // only partial keys) — clear the mark only when we still have deltas.
      const waves = AGGREGATE_WAVES[tab] ?? [];
      const anyNeeded = waves.some((wave) => filterNeededKeys(wave).length > 0);
      if (!anyNeeded) {
        session.fetchedTabs[tab] = true;
        return;
      }
      session.fetchedTabs[tab] = false;
      dispatchTabWaves({ dispatch, customerId, tab });
    });
  });

  scheduleStep(generation, headroom + IDLE_PREFETCH_STAGGER_MS * (step + 1), () => {
    warmSaleLeadDownload(dispatch, customerId);
  });
};

/** Mark sale-lead warm already started (e.g. Sales Marketing fallback). */
export const markSaleLeadRequested = (customerId: string | number) => {
  ensureClient(customerId);
  session.saleLeadRequested = true;
};

export const isSaleLeadRequested = (customerId: string | number): boolean => {
  ensureClient(customerId);
  return session.saleLeadRequested;
};
